import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { fetchClaimData, fetchClaimDownloadData } from "../apis/claimService";
import {
  IClaimDownload,
  IClaimFilter,
  IClaimResponse,
  IClaimResponseForm,
  IClaimState,
} from "../interfaces/IClaim";
import { LIST_DEFAULT_PAGE_SIZE } from "../utils/constants";

const initialState: IClaimState = {
  totalAvailableClaims: null,
  isFetchingAllClaims: false,
  error: null,
  claimFilters: {
    to: "",
    responseDataFormat: "JSON",
    portal: "ihx",
    gsigmaProviderId: 1,
    from: "",
    searchCriteria: null,
    portalStatus: "all",
    dateFilter: "doa",
  },
  retainedClaimFilters: {
    pageNumber: 1,
    pageSize: LIST_DEFAULT_PAGE_SIZE,
  },
};

export const getClaimData = createAsyncThunk(
  "claims/getClaimData",
  async ({
    filters,
    pageNumber,
    pageSize,
  }: {
    filters: IClaimFilter;
    pageNumber: number | null;
    pageSize: number;
  }) => {
    if (pageNumber) {
      const response = await fetchClaimData(filters, pageNumber, pageSize);
      return response;
    }
  }
);
export const downloadClaimList = createAsyncThunk(
  "claims/downloadClaimList",
  async ({ body }: { body: IClaimDownload }) => {
    if (body) {
      const response = await fetchClaimDownloadData(body);
      return response;
    }
  }
);

const claimSlice = createSlice({
  name: "claims",
  initialState,
  reducers: {
    setClaimFilters: (state, action: PayloadAction<IClaimFilter>) => {
      state.claimFilters = action.payload;
    },
    setRetainedClaimFilters: (state, action) => {
      state.retainedClaimFilters = action.payload;
    },
    clearPageToClaimIdMap: (state) => {
      state.totalAvailableClaims = null;
    },
    updatePortal: (state, action: PayloadAction<string>) => {
      state.claimFilters.portal = action.payload;
    },
    updatePortalStatus: (state, action: PayloadAction<string>) => {
      state.claimFilters.portalStatus = action.payload;
    },
    updateDateFilter: (state, action: PayloadAction<string>) => {
      state.claimFilters.dateFilter = action.payload;
    },
    setDates: (state, action: PayloadAction<any>) => {
      state.claimFilters.from = action.payload.from;
      state.claimFilters.to = action.payload.to;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getClaimData.pending, (state) => {
        state.isFetchingAllClaims = true;
        state.error = null;
      })
      .addCase(
        getClaimData.fulfilled,
        (state, action: PayloadAction<IClaimResponseForm>) => {
          state.isFetchingAllClaims = false;
          state.totalAvailableClaims = action.payload;
          state.error = null;
        }
      )
      .addCase(getClaimData.rejected, (state, action) => {
        state.isFetchingAllClaims = false;
        state.error = action.error.message || "Failed to fetch claim data";
      });
  },
});

export const {
  updatePortal,
  setClaimFilters,
  setRetainedClaimFilters,
  clearPageToClaimIdMap,
  updatePortalStatus,
  updateDateFilter,
  setDates,
} = claimSlice.actions;
export default claimSlice.reducer;
